import {MonitoringService} from '../monitoring.service';
import {CommonModule} from '@angular/common';
import {ChangeDetectionStrategy, Component, inject, viewChildren} from '@angular/core';
import {FormBuilder, FormControl, ReactiveFormsModule} from '@angular/forms';
import {DatePipe, DateTimePipe, LoadComponent, PaginationComponent} from '@brunata-angular/ui';
import {
    comparator,
    filterNulls,
    fromControl,
    HiddenValueWhileLoading,
    includesIgnoreCase,
    keyComparator,
    switchMapWithLoading,
    TranslatePipe,
    TranslateService,
} from '@brunata-angular/util';
import {InputDateGroupComponent} from '@brunata-angular/ui';
import {InputTextGroupComponent} from '@brunata-angular/ui';
import {applySortingToHeaders, SortableTableHeaderComponent, Sorting} from '@brunata-angular/ui';
import {DateTime} from 'luxon';
import {
    BehaviorSubject,
    combineLatest,
    distinctUntilChanged,
    filter,
    map,
    Observable,
    tap,
} from 'rxjs';
import {NotificationItem} from './notification.model';
import {InputDropdownCheckboxesComponent, SelectableItem} from '@brunata-angular/ui';
import {DateRange, InputDateRangePickerComponent} from '@brunata-angular/ui';
import {NgbModal, NgbPopover} from '@ng-bootstrap/ng-bootstrap';
import {NotificationDetailsModalComponent} from './notification-details-modal/notification-details-modal.component';
import {Store} from '@ngrx/store';
import {selectBuilding} from 'src/app/state/selectors';
import {uniqBy} from 'lodash-es';

@Component({
    selector: 'app-notification-history',
    templateUrl: './notification-history.component.html',
    styleUrl: './notification-history.component.scss',
    imports: [
        InputDateGroupComponent,
        CommonModule,
        TranslatePipe,
        LoadComponent,
        ReactiveFormsModule,
        SortableTableHeaderComponent,
        DateTimePipe,
        DatePipe,
        PaginationComponent,
        InputTextGroupComponent,
        InputDateGroupComponent,
        InputDropdownCheckboxesComponent,
        InputDateRangePickerComponent,
        NgbPopover,
    ],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NotificationHistoryComponent {
    private readonly headers = viewChildren(SortableTableHeaderComponent);

    private readonly store = inject(Store);

    pageSize = 20;
    page: BehaviorSubject<number> = new BehaviorSubject<number>(1);

    readonly formGroup = this.formBuilder.group({
        dateRange: new FormControl<DateRange>({
            startDate: DateTime.now().minus({months: 1}),
            endDate: DateTime.now(),
        }),
    });

    readonly formGroupFilter = this.formBuilder.group({
        notificationTypes: new FormControl<SelectableItem[]>([]),
        name: new FormControl<string | null>(''),
        notificationSent: new FormControl<DateTime | null>(null),
        recipientEmails: new FormControl<string | null>(''),
    });

    readonly startDate$: Observable<DateTime | null> = fromControl(
        this.formGroup.controls.dateRange,
    ).pipe(
        filterNulls(),
        map(dateRange => dateRange.startDate),
    );

    private readonly notifications$: Observable<HiddenValueWhileLoading<NotificationItem[]>> =
        combineLatest([
            this.store.select(selectBuilding).pipe(
                map(buildingState =>
                    buildingState.status === 'success' ? buildingState.buildingNo : null,
                ),
                filterNulls(),
            ),
            fromControl(this.formGroup.controls.dateRange).pipe(
                filter(
                    (dateRange): dateRange is {startDate: DateTime; endDate: DateTime} =>
                        dateRange?.startDate != null && dateRange.endDate != null,
                ),
                filter(dateRange => dateRange.startDate < dateRange.endDate),
            ),
        ]).pipe(
            distinctUntilChanged(),
            switchMapWithLoading(([buildingNo, dateRange]) =>
                this.monitoringService
                    .getNotificationHistory(buildingNo, dateRange.startDate, dateRange.endDate)
                    .pipe(
                        tap(notifications => {
                            const uniqueEventTypes = this.getUniqueValuesForLists(notifications);
                            this.formGroupFilter.controls.notificationTypes.patchValue(
                                uniqueEventTypes,
                            );
                        }),
                        tap(() =>
                            setTimeout(
                                () => this.onSort({column: 'notificationSent', direction: 'desc'}),
                                0,
                            ),
                        ),
                    ),
            ),
        );
    readonly notificationsFilteredAndSorted$ = combineLatest([
        this.notifications$,
        this.monitoringService.sorting,
        fromControl(this.formGroupFilter),
    ]).pipe(
        map(([tableSettings, sorting, filterControl]) => {
            if (tableSettings.loading) {
                return tableSettings;
            }
            return {
                loading: false as const,
                value: [...tableSettings.value]
                    .filter(notification => this.notificationFilter(filterControl, notification))
                    .sort(this.notificationsComparer(sorting)),
            };
        }),
    );

    constructor(
        private formBuilder: FormBuilder,
        private monitoringService: MonitoringService,
        private translateService: TranslateService,
        private modalService: NgbModal,
    ) {}

    openNotificationModal(emailLogId: number): void {
        const modalRef = this.modalService.open(NotificationDetailsModalComponent, {size: 'lg'});
        const instance = modalRef.componentInstance as NotificationDetailsModalComponent;
        instance.emailLogId.next(emailLogId);
    }

    setPage(page: number): void {
        this.page.next(page);
    }

    onSort(sorting: Sorting): void {
        applySortingToHeaders(this.headers(), sorting);
        this.monitoringService.updateSorting(sorting);
    }

    private getUniqueValuesForLists(notifications: NotificationItem[]): SelectableItem[] {
        const notificationTypes = notifications.map(notification => ({
            name:
                notification.translationKey != null
                    ? this.translateService.translate(
                          notification.translationKey,
                          'online',
                          notification.module,
                      )
                    : '',
            selected: false,
        }));
        return uniqBy(notificationTypes, notificationType => notificationType.name).sort(
            keyComparator(notificationType => notificationType.name),
        );
    }

    private notificationsComparer(
        sorting: Sorting,
    ): (a: NotificationItem, b: NotificationItem) => number {
        return comparator({
            selector:
                sorting.column === 'combinedType'
                    ? (item: NotificationItem): string =>
                          item.translationKey != null
                              ? this.translateService.translate(
                                    item.translationKey,
                                    'online',
                                    item.module,
                                )
                              : ''
                    : (item: NotificationItem): string | number | Date =>
                          item[sorting.column as keyof NotificationItem] != null
                              ? (item[sorting.column as keyof NotificationItem] as
                                    | string
                                    | number
                                    | Date)
                              : '',
            order: sorting.direction,
        });
    }

    private notificationFilter(
        filterControl: Partial<{
            notificationTypes: SelectableItem[] | null;
            name: string | null;
            notificationSent: DateTime | null;
            recipientEmails: string | null;
        }>,
        notification: NotificationItem,
    ): boolean {
        const valuesToFilter =
            filterControl.notificationTypes
                ?.filter(it => it.selected)
                .map(it => it.name.toString()) ?? [];
        let compareKey: string;

        switch (notification.translationKey) {
            case null:
            case '':
                compareKey = '';
                break;
            default:
                compareKey = this.translateService.translate(
                    notification.translationKey,
                    'online',
                    notification.module,
                );
                break;
        }
        const isNotificationTypeValid =
            valuesToFilter.length === 0 ||
            valuesToFilter.includes(compareKey.length > 0 ? compareKey : '');

        const isNameValid =
            filterControl.name == null || includesIgnoreCase(notification.name, filterControl.name);

        const isNotificationSentValid =
            filterControl.notificationSent === null ||
            notification.notificationSent.toISODate() ===
                filterControl.notificationSent?.toISODate();

        const isRecipientEmailsValid =
            filterControl.recipientEmails == null ||
            includesIgnoreCase(notification.recipientEmails, filterControl.recipientEmails);

        return (
            isNotificationTypeValid &&
            isNameValid &&
            isNotificationSentValid &&
            isRecipientEmailsValid
        );
    }
}
